import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Play, History, FileCode, Bot, Grid3x3, List as ListIcon, Filter, Edit, Trash2, Copy, CheckSquare, Square, Search, X, Eye, Clock, Sparkles, Settings, Crown, LogOut, User, FolderOpen, Calendar, BarChart3, Tag, Users, Zap } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import NewAPITestModal from "@/components/NewAPITestModal";
import TrialBanner from "@/components/TrialBanner";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { StatsCards } from "@/components/dashboard/StatsCards";
import { RecentExecutions } from "@/components/dashboard/RecentExecutions";
import { TestDetailsModal } from "@/components/dashboard/TestDetailsModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [confirm, confirmDialog] = useConfirm();
  const [testCases, setTestCases] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [contentMode, setContentMode] = useState("test-cases"); // test-cases or test-suites
  const [browserFilter, setBrowserFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTests, setSelectedTests] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTestCase, setSelectedTestCase] = useState(null);
  const [testCaseExecutions, setTestCaseExecutions] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showNewAPIModal, setShowNewAPIModal] = useState(false);
  const [planStatus, setPlanStatus] = useState(null);

  // Tag filtering
  const [allTags, setAllTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [showTagFilter, setShowTagFilter] = useState(false);

  // Check if we need to handle OAuth callback - this is a fallback in case AppRouter doesn't catch it
  useEffect(() => {
    const hash = window.location.hash;
    if (hash?.includes('session_id=') || hash?.includes('session_token=')) {
      console.log('Dashboard detected OAuth hash, redirecting to auth callback');
      navigate('/auth/callback' + hash, { replace: true });
    }
  }, [navigate]);

  // Auth guard: redirect to home if not authenticated after auth check completes
  useEffect(() => {
    if (!authLoading && !user) {
      console.log('[Dashboard] No authenticated user, redirecting to /');
      navigate('/', { replace: true });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    fetchData();
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserFilter, statusFilter, searchQuery, selectedTags, contentMode]);

  // Fetch plan / billing status once on mount
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/billing/status`, { withCredentials: true })
      .then(res => setPlanStatus(res.data))
      .catch(() => {}); // non-critical — silently ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    try {
      const browserParam = browserFilter !== "all" ? `browser=${browserFilter}` : "";
      const statusParam = statusFilter !== "all" ? `status=${statusFilter}` : "";
      const searchParam = searchQuery ? `search=${encodeURIComponent(searchQuery)}` : "";
      const tagsParam = selectedTags.length > 0 ? `tags=${selectedTags.join(",")}` : "";
      const params = [browserParam, statusParam, searchParam, tagsParam].filter(p => p).join("&");
      
      if (contentMode === "test-cases") {
        const [casesRes, executionsRes] = await Promise.all([
          axios.get(`${API}/test-cases${params ? `?${params}` : ""}`, { withCredentials: true }),
          axios.get(`${API}/executions${statusParam ? `?${statusParam}` : ""}`, { withCredentials: true })
        ]);
        // Only show UI tests (not API tests) in the Test Cases tab
        const allCases = Array.isArray(casesRes.data) ? casesRes.data : [];
        setTestCases(allCases.filter(tc => !tc.test_type || tc.test_type === "ui"));
        setExecutions(Array.isArray(executionsRes.data) ? executionsRes.data : []);
      } else if (contentMode === "api-tests") {
        // Fetch API tests only
        const casesRes = await axios.get(`${API}/test-cases${params ? `?${params}` : ""}`, { withCredentials: true });
        const allCases = Array.isArray(casesRes.data) ? casesRes.data : [];
        setTestCases(allCases.filter(tc => tc.test_type === "api"));
      } else {
        // Fetch test suites
        const suitesRes = await axios.get(`${API}/suites${params ? `?${params}` : ""}`, { withCredentials: true });
        setTestSuites(Array.isArray(suitesRes.data) ? suitesRes.data : []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const endpoint = contentMode === "test-cases" ? "/test-cases/tags/all" : "/suites/tags/all";
      const response = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const runTest = async (testCaseId) => {
    try {
      toast.info('Starting test execution...');
      const response = await axios.post(`${API}/executions?test_case_id=${testCaseId}`, {}, { withCredentials: true });
      toast.success('Test execution started!');
      navigate(`/execution/${response.data.id}`);
    } catch (error) {
      console.error('Failed to run test:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402) {
        const msg = typeof detail === 'object' ? detail.message : detail;
        toast.error(msg || 'You have used all your free test runs this month. Please upgrade.');
      } else {
        toast.error('Failed to start test execution');
      }
    }
  };

  const deleteTest = async (testCaseId) => {
    const ok = await confirm({
      title: 'Delete this test case?',
      description: 'The test case and its saved steps will be permanently removed. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/test-cases/${testCaseId}`, { withCredentials: true });
      toast.success('Test case deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete test:', error);
      toast.error('Failed to delete test case');
    }
  };

  const deleteExecution = async (executionId) => {
    const ok = await confirm({
      title: 'Delete this execution?',
      description: 'This execution record, including its logs and screenshots, will be permanently removed.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/executions/${executionId}`, { withCredentials: true });
      toast.success('Execution deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete execution:', error);
      toast.error('Failed to delete execution');
    }
  };

  const duplicateTest = async (testCaseId) => {
    try {
      const response = await axios.get(`${API}/test-cases/${testCaseId}`, { withCredentials: true });
      const testCase = response.data;
      await axios.post(`${API}/test-cases`, {
        ...testCase,
        name: `${testCase.name} (Copy)`,
        id: undefined,
        created_at: undefined
      }, { withCredentials: true });
      toast.success('Test case duplicated!');
      fetchData();
    } catch (error) {
      console.error('Failed to duplicate test:', error);
      toast.error('Failed to duplicate test case');
    }
  };

  const toggleTestSelection = (testId) => {
    setSelectedTests(prev => 
      prev.includes(testId) ? prev.filter(id => id !== testId) : [...prev, testId]
    );
  };

  const runSelectedTests = async () => {
    if (selectedTests.length === 0) {
      toast.error('No tests selected');
      return;
    }
    try {
      toast.info(`Starting ${selectedTests.length} test executions...`);
      await Promise.all(selectedTests.map(id => axios.post(`${API}/executions?test_case_id=${id}`, {}, { withCredentials: true })));
      toast.success(`${selectedTests.length} tests started!`);
      setSelectedTests([]);
      setBulkMode(false);
      fetchData();
    } catch (error) {
      console.error('Failed to run tests:', error);
      toast.error('Failed to start some executions');
    }
  };

  const deleteSelectedTests = async () => {
    if (selectedTests.length === 0) {
      toast.error('No tests selected');
      return;
    }
    const ok = await confirm({
      title: `Delete ${selectedTests.length} test case${selectedTests.length > 1 ? 's' : ''}?`,
      description: 'All selected test cases and their saved steps will be permanently removed. This cannot be undone.',
      confirmLabel: `Delete ${selectedTests.length}`,
      destructive: true,
    });
    if (!ok) return;
    try {
      await Promise.all(selectedTests.map(id => axios.delete(`${API}/test-cases/${id}`, { withCredentials: true })));
      toast.success(`${selectedTests.length} test cases deleted`);
      setSelectedTests([]);
      setBulkMode(false);
      fetchData();
    } catch (error) {
      console.error('Failed to delete tests:', error);
      toast.error('Failed to delete some test cases');
    }
  };

  const viewTestCaseDetails = async (testCaseId) => {
    try {
      const [testCaseRes, executionsRes] = await Promise.all([
        axios.get(`${API}/test-cases/${testCaseId}`, { withCredentials: true }),
        axios.get(`${API}/test-cases/${testCaseId}/executions`, { withCredentials: true })
      ]);
      setSelectedTestCase(testCaseRes.data);
      setTestCaseExecutions(executionsRes.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Failed to fetch test case details:', error);
      toast.error('Failed to load test case details');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'running': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'failed': return 'bg-red-500/10 text-red-400 border-red-500/20';
      default: return 'bg-slate-800 text-slate-300 border-slate-700';
    }
  };

  // Show spinner while auth is loading or user is being validated
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#00b37a] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render dashboard content if not authenticated (will redirect via useEffect)
  if (!user) return null;

  return (
    <TooltipProvider delayDuration={200}>
    {confirmDialog}
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="dashboard-page">
      <AppNav />
      <div>
        {/* Navigation placeholder so rest of structure stays intact */}
        <nav className="hidden">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-[#00E599] rounded-sm flex items-center justify-center">
                <Bot className="w-6 h-6 text-black" />
              </div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif' }}>AutoQA.ai</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                data-testid="nav-analytics-btn"
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                onClick={() => navigate('/analytics')}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
              <Button 
                data-testid="nav-suites-btn"
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                onClick={() => navigate('/suites')}
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Suites
              </Button>
              <Button 
                data-testid="nav-schedules-btn"
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                onClick={() => navigate('/schedules')}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Schedules
              </Button>
              <Button 
                data-testid="nav-history-btn"
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                onClick={() => navigate('/history')}
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
              <Button 
                data-testid="nav-generate-from-story-btn"
                className="bg-[#D946EF] text-white hover:bg-[#C026D3] rounded-sm font-semibold"
                onClick={() => navigate('/generate-from-story')}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate from Story
              </Button>
              <Button 
                data-testid="nav-create-test-btn"
                className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold neon-glow"
                onClick={() => navigate('/create-test')}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Test
              </Button>
              
              {/* User Menu */}
              {user && (
                <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/10">
                  {user.role === 'super_admin' && (
                    <Button
                      data-testid="nav-admin-btn"
                      variant="ghost"
                      className="text-[#D946EF] hover:text-[#D946EF] hover:bg-[#D946EF]/10 rounded-sm"
                      onClick={() => navigate('/admin')}
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Admin
                    </Button>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        data-testid="nav-teams-btn"
                        variant="ghost"
                        className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                        onClick={() => navigate('/teams')}
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-800 text-white border-slate-700">
                      Teams
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        data-testid="nav-settings-btn"
                        variant="ghost"
                        className="text-slate-400 hover:text-white hover:bg-white/5 rounded-sm"
                        onClick={() => navigate('/settings')}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-800 text-white border-slate-700">
                      Settings
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        data-testid="nav-logout-btn"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-sm"
                        onClick={logout}
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="bg-slate-800 text-white border-slate-700">
                      Logout
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <div className="px-6 py-8 md:px-10">
          <div className="max-w-screen-xl mx-auto">
            {/* Trial Banner */}
            {planStatus && (
              <div className="mb-6">
                <TrialBanner planStatus={planStatus} />
              </div>
            )}

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold tracking-tight mb-1 text-slate-900" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Dashboard
              </h1>
              <p className="text-slate-500 text-sm">Manage and monitor your test automation</p>
            </div>

            {/* Stats */}
            <StatsCards testCasesCount={testCases.length} executions={executions} loading={loading} />

            {/* Test Cases */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4 gap-4">
                <h2 className="text-xl font-semibold whitespace-nowrap text-slate-900" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {contentMode === "test-cases" ? "Test Cases" : contentMode === "api-tests" ? "API Tests" : "Test Suites"}
                </h2>
                <div className="flex items-center gap-3 flex-wrap justify-end">
                  {/* Bulk Mode Toggle */}
                  {testCases.length > 0 && (
                    <Button
                      data-testid="bulk-mode-btn"
                      size="sm"
                      variant={bulkMode ? "default" : "ghost"}
                      className={bulkMode ? "bg-[#D946EF] text-white" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}
                      onClick={() => {
                        setBulkMode(!bulkMode);
                        setSelectedTests([]);
                      }}
                    >
                      <CheckSquare className="w-4 h-4 mr-2" />
                      {bulkMode ? 'Cancel' : 'Select'}
                    </Button>
                  )}
                  
                  {/* Bulk Actions */}
                  {bulkMode && selectedTests.length > 0 && (
                    <>
                      <Button
                        data-testid="run-selected-btn"
                        size="sm"
                        className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm"
                        onClick={runSelectedTests}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Run ({selectedTests.length})
                      </Button>
                      <Button
                        data-testid="delete-selected-btn"
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm"
                        onClick={deleteSelectedTests}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete ({selectedTests.length})
                      </Button>
                    </>
                  )}
                  
                  {/* Search Box */}
                  {!bulkMode && (
                    <div className="relative w-[200px]">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        data-testid="search-test-cases"
                        placeholder="Search tests..."
                        className="pl-10 pr-8 bg-white border-slate-200 text-slate-900 rounded-md"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Content Mode Toggle */}
                  {!bulkMode && (
                    <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            data-testid="test-cases-mode-btn"
                            size="sm"
                            variant={contentMode === "test-cases" ? "default" : "ghost"}
                            className={contentMode === "test-cases" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}
                            onClick={() => { setContentMode("test-cases"); setSelectedTags([]); }}
                          >
                            <FileCode className="w-4 h-4 mr-1" />
                            Cases
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-slate-800 text-white border-slate-700">
                          View Test Cases
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            data-testid="test-suites-mode-btn"
                            size="sm"
                            variant={contentMode === "test-suites" ? "default" : "ghost"}
                            className={contentMode === "test-suites" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}
                            onClick={() => { setContentMode("test-suites"); setSelectedTags([]); }}
                          >
                            <FolderOpen className="w-4 h-4 mr-1" />
                            Suites
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-slate-800 text-white border-slate-700">
                          View Test Suites
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            data-testid="api-tests-mode-btn"
                            size="sm"
                            variant={contentMode === "api-tests" ? "default" : "ghost"}
                            className={contentMode === "api-tests" ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-900"}
                            onClick={() => { setContentMode("api-tests"); setSelectedTags([]); }}
                          >
                            <Zap className="w-4 h-4 mr-1" />
                            API
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-slate-800 text-white border-slate-700">
                          View API Tests
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                  
                  {/* Tag Filter */}
                  {!bulkMode && allTags.length > 0 && (
                    <div className="relative">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            data-testid="tag-filter-btn"
                            size="sm"
                            variant={selectedTags.length > 0 ? "default" : "outline"}
                            className={selectedTags.length > 0
                              ? "bg-[#D946EF] text-white hover:bg-[#C026D3]"
                              : "border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                            }
                            onClick={() => setShowTagFilter(!showTagFilter)}
                          >
                            <Tag className="w-4 h-4 mr-1" />
                            {selectedTags.length > 0 ? `${selectedTags.length} Tags` : "Tags"}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-slate-800 text-white border-slate-700">
                          Filter by Tags
                        </TooltipContent>
                      </Tooltip>
                      
                      {/* Tag Filter Dropdown */}
                      {showTagFilter && (
                        <div className="absolute top-full mt-2 right-0 z-50 w-64 bg-white border border-slate-200 rounded-xl p-3 shadow-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-800">Filter by Tags</span>
                            {selectedTags.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-xs text-red-400 hover:text-red-300 h-6 px-2"
                                onClick={() => setSelectedTags([])}
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                            {allTags.map((tag) => (
                              <Badge
                                key={tag}
                                className={`cursor-pointer transition-colors ${
                                  selectedTags.includes(tag)
                                    ? "bg-[#D946EF] text-white border-[#D946EF]"
                                    : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                                }`}
                                onClick={() => {
                                  setSelectedTags(prev => 
                                    prev.includes(tag) 
                                      ? prev.filter(t => t !== tag)
                                      : [...prev, tag]
                                  );
                                }}
                              >
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Browser Filter */}
                  {!bulkMode && contentMode === "test-cases" && (
                    <Select value={browserFilter} onValueChange={setBrowserFilter}>
                      <SelectTrigger data-testid="browser-filter" className="w-[140px] bg-white border-slate-200 text-slate-700 rounded-md">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="all">All Browsers</SelectItem>
                        <SelectItem value="chromium">Chromium</SelectItem>
                        <SelectItem value="firefox">Firefox</SelectItem>
                        <SelectItem value="webkit">WebKit</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  {/* View Toggle */}
                  {!bulkMode && (
                    <div className="flex gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            data-testid="grid-view-btn"
                            size="sm"
                            variant={viewMode === "grid" ? "default" : "ghost"}
                            className={viewMode === "grid" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}
                            onClick={() => setViewMode("grid")}
                          >
                            <Grid3x3 className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-slate-800 text-white border-slate-700">
                          Grid View
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            data-testid="list-view-btn"
                            size="sm"
                            variant={viewMode === "list" ? "default" : "ghost"}
                            className={viewMode === "list" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"}
                            onClick={() => setViewMode("list")}
                          >
                            <ListIcon className="w-4 h-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-slate-800 text-white border-slate-700">
                          List View
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
                  
                  <Button
                    data-testid="create-new-test-btn"
                    className={contentMode === "api-tests" ? "bg-blue-500 text-white hover:bg-blue-600 rounded-md font-semibold" : "bg-[#00b37a] text-white hover:bg-[#009966] rounded-md font-semibold"}
                    onClick={() => contentMode === "api-tests" ? setShowNewAPIModal(true) : navigate('/create-test')}
                  >
                    {contentMode === "api-tests" ? <Zap className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    {contentMode === "api-tests" ? "New API Test" : "Create Test"}
                  </Button>
                </div>
              </div>

              {/* Content based on mode */}
              {loading ? (
                <p className="text-slate-400">Loading...</p>
              ) : contentMode === "test-cases" ? (
                /* Test Cases View */
                testCases.length === 0 ? (
                  <Card className="glass-card rounded-xl">
                    <CardContent className="p-12 text-center">
                      <FileCode className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">No test cases yet</h3>
                      <p className="text-slate-400 mb-6">Create your first AI-powered test case to get started</p>
                      <Button 
                        data-testid="empty-state-create-btn"
                        className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-md font-semibold"
                        onClick={() => navigate('/create-test')}
                      >
                        Create Test Case
                      </Button>
                    </CardContent>
                  </Card>
                ) : viewMode === "grid" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {testCases.map((testCase, idx) => (
                    <motion.div
                      key={testCase.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Card 
                        className="glass-card rounded-xl hover:shadow-md transition-all cursor-pointer" 
                        data-testid={`test-case-${testCase.id}`}
                        onClick={() => !bulkMode && viewTestCaseDetails(testCase.id)}
                      >
                        <CardHeader>
                          <div className="flex items-start gap-3">
                            {bulkMode && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTestSelection(testCase.id);
                                }}
                                className="mt-1"
                                data-testid={`checkbox-${testCase.id}`}
                              >
                                {selectedTests.includes(testCase.id) ? (
                                  <CheckSquare className="w-5 h-5 text-[#00E599]" />
                                ) : (
                                  <Square className="w-5 h-5 text-slate-500" />
                                )}
                              </button>
                            )}
                            <div className="flex-1">
                              <CardTitle className="text-lg" style={{ fontFamily: 'Unbounded, sans-serif' }}>{testCase.name}</CardTitle>
                              <CardDescription className="text-slate-500">{testCase.description}</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="flex items-center justify-between">
                            <div className="flex gap-2 flex-wrap">
                              {testCase.test_type === "api" ? (
                                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                                  <Zap className="w-3 h-3 mr-1" />API Test
                                </Badge>
                              ) : (
                                <>
                                  <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                                    {(testCase.steps || []).length} steps
                                  </Badge>
                                  <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                                    {testCase.browser}
                                  </Badge>
                                </>
                              )}
                            </div>
                            {!bulkMode && (
                              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      data-testid={`duplicate-test-btn-${testCase.id}`}
                                      size="sm"
                                      variant="ghost"
                                      className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                                      onClick={() => duplicateTest(testCase.id)}
                                    >
                                      <Copy className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Duplicate
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      data-testid={`edit-test-btn-${testCase.id}`}
                                      size="sm"
                                      variant="ghost"
                                      className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                                      onClick={() => navigate(testCase.test_type === "api" ? `/edit-api-test/${testCase.id}` : `/edit-test/${testCase.id}`)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Edit
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      data-testid={`delete-test-btn-${testCase.id}`}
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm"
                                      onClick={() => deleteTest(testCase.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Delete
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      data-testid={`run-test-btn-${testCase.id}`}
                                      size="sm"
                                      className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-md font-semibold"
                                      onClick={() => runTest(testCase.id)}
                                    >
                                      <Play className="w-4 h-4 mr-1" />
                                      Run
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Run Test
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {testCases.map((testCase, idx) => (
                    <motion.div
                      key={testCase.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.03 }}
                    >
                      <Card
                        className="glass-card rounded-xl hover:shadow-md transition-all cursor-pointer"
                        data-testid={`test-case-list-${testCase.id}`}
                        onClick={() => !bulkMode && viewTestCaseDetails(testCase.id)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 flex-1">
                              {bulkMode && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleTestSelection(testCase.id); }}
                                  data-testid={`checkbox-list-${testCase.id}`}
                                >
                                  {selectedTests.includes(testCase.id) ? (
                                    <CheckSquare className="w-5 h-5 text-[#00E599]" />
                                  ) : (
                                    <Square className="w-5 h-5 text-slate-500" />
                                  )}
                                </button>
                              )}
                              <div className="w-10 h-10 bg-[#00b37a]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                <FileCode className="w-5 h-5 text-[#00b37a]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold mb-1 truncate">{testCase.name}</h3>
                                <p className="text-sm text-slate-400 truncate">{testCase.description}</p>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                                  {testCase.steps.length} steps
                                </Badge>
                                <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                                  {testCase.browser}
                                </Badge>
                              </div>
                            </div>
                            {!bulkMode && (
                              <div className="flex gap-2 ml-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      data-testid={`duplicate-test-list-btn-${testCase.id}`}
                                      size="sm"
                                      variant="ghost"
                                      className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                                      onClick={() => duplicateTest(testCase.id)}
                                    >
                                      <Copy className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Duplicate
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      data-testid={`edit-test-list-btn-${testCase.id}`}
                                      size="sm"
                                      variant="ghost"
                                      className="text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                                      onClick={() => navigate(testCase.test_type === "api" ? `/edit-api-test/${testCase.id}` : `/edit-test/${testCase.id}`)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Edit
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      data-testid={`delete-test-list-btn-${testCase.id}`}
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm"
                                      onClick={() => deleteTest(testCase.id)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Delete
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      data-testid={`run-test-list-btn-${testCase.id}`}
                                      size="sm"
                                      className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-md font-semibold"
                                      onClick={() => runTest(testCase.id)}
                                    >
                                      <Play className="w-4 h-4 mr-1" />
                                      Run
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Run Test
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )
              ) : (
                /* Test Suites View */
                testSuites.length === 0 ? (
                  <Card className="glass-card rounded-xl">
                    <CardContent className="p-12 text-center">
                      <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">No test suites yet</h3>
                      <p className="text-slate-400 mb-6">Test suites help organize related test cases for regression testing</p>
                      <Button 
                        data-testid="empty-state-create-suite-btn"
                        className="bg-[#F59E0B] text-black hover:bg-[#D97706] rounded-sm font-semibold"
                        onClick={() => navigate('/suites')}
                      >
                        Go to Test Suites
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {testSuites.map((suite, idx) => (
                      <motion.div
                        key={suite.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                      >
                        <Card 
                          className="glass-card rounded-xl hover:shadow-md transition-all cursor-pointer"
                          data-testid={`suite-${suite.id}`}
                          onClick={() => navigate(`/suites`)}
                        >
                          <CardHeader>
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-[#F59E0B]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                                <FolderOpen className="w-5 h-5 text-[#F59E0B]" />
                              </div>
                              <div className="flex-1">
                                <CardTitle className="text-lg" style={{ fontFamily: 'Unbounded, sans-serif' }}>{suite.name}</CardTitle>
                                <CardDescription className="text-slate-400">{suite.description || 'No description'}</CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <Badge className="bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20">
                                  {suite.test_case_ids?.length || 0} tests
                                </Badge>
                                {suite.tags && suite.tags.length > 0 && (
                                  <div className="flex gap-1">
                                    {suite.tags.slice(0, 2).map((tag) => (
                                      <Badge key={tag} className="bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20 text-xs">
                                        {tag}
                                      </Badge>
                                    ))}
                                    {suite.tags.length > 2 && (
                                      <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">
                                        +{suite.tags.length - 2}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      size="sm"
                                      className="bg-[#00b37a] text-white hover:bg-[#009966] rounded-md font-semibold"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/suites`);
                                      }}
                                    >
                                      <Play className="w-4 h-4 mr-1" />
                                      Run Suite
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    Run all tests in suite
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )
              )}
            </div>

            {/* Recent Executions - Only show in test-cases mode */}
            {contentMode === "test-cases" && (
              <RecentExecutions
                executions={executions}
                getStatusColor={getStatusColor}
                onOpenExecution={(id) => navigate(`/execution/${id}`)}
                onDeleteExecution={deleteExecution}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Test Case Details Modal */}
      <TestDetailsModal
        open={showDetailsModal}
        onOpenChange={setShowDetailsModal}
        testCase={selectedTestCase}
        testCaseExecutions={testCaseExecutions}
        getStatusColor={getStatusColor}
        onEdit={(id) => {
          setShowDetailsModal(false);
          navigate(`/edit-test/${id}`);
        }}
        onRun={(id) => {
          setShowDetailsModal(false);
          runTest(id);
        }}
        onOpenExecution={(id) => {
          setShowDetailsModal(false);
          navigate(`/execution/${id}`);
        }}
      />

      {/* New API Test entry-point modal */}
      <NewAPITestModal
        open={showNewAPIModal}
        onClose={() => setShowNewAPIModal(false)}
      />

      {/* Paywall is handled globally by PlanGate in App.js */}
    </div>
    </TooltipProvider>
  );
}